import type {
  FormulaConditionInput as CommonFormulaConditionInput,
  FormulaInput as CommonFormulaInput,
  FormulaValueInput as CommonFormulaValueInput,
  LogicExpressionInput as CommonLogicExpressionInput,
  LogicExpressionValueInput as CommonLogicExpressionValueInput,
} from '@doss/common-all'
import { FormulaOperator, LogicExpressionCondition, LogicExpressionType } from '@doss/prisma/types'
import { z } from 'zod'
import type {
  FormulaConditionInput as GqlFormulaConditionInput,
  FormulaValueInput as GqlFormulaValueInput,
  LogicExpressionInput as GqlLogicExpressionInput,
  LogicExpressionValueInput as GqlLogicExpressionValueInput,
  ParentFormulaInput,
} from '~/common/generated/apollo-operations'

// Define schemas matching the exact structure
const logicExpressionValueInputSchema: z.ZodType<CommonLogicExpressionValueInput> = z.lazy(() =>
  z.object({
    order: z.number(),
    value: z.record(z.string(), z.unknown()).nullable().optional(),
    columnId: z.string().nullable().optional(),
    nestedExpression: logicExpressionInputSchema.nullable().optional(),
    formula: formulaInputSchema.nullable().optional(),
    formFieldId: z.string().nullable().optional(),
    formInputId: z.string().nullable().optional(),
  }),
)

const logicExpressionInputSchema: z.ZodType<CommonLogicExpressionInput> = z.lazy(() =>
  z.object({
    type: z.string() as z.ZodType<LogicExpressionType>,
    condition: z.string().nullable().optional() as z.ZodType<
      LogicExpressionCondition | null | undefined
    >,
    values: z.array(logicExpressionValueInputSchema),
    logicExpressionString: z.string().nullable().optional(),
  }),
)

const formulaConditionInputSchema: z.ZodType<CommonFormulaConditionInput> = z.lazy(() =>
  z.object({
    condition: logicExpressionInputSchema,
    order: z.number(),
    formula: formulaInputSchema,
  }),
)

const formulaValueInputSchema: z.ZodType<CommonFormulaValueInput> = z.lazy(() =>
  z.object({
    tableColumnId: z.string().nullable().optional(),
    formInputId: z.string().nullable().optional(),
    formula: formulaInputSchema.nullable().optional(),
    conditions: z.array(formulaConditionInputSchema).nullable().optional(),
    value: z.number().nullable().optional(),
    valueNumber: z.number().nullable().optional(),
    valueString: z.string().nullable().optional(),
    order: z.number(),
  }),
)

const formulaInputSchema: z.ZodType<CommonFormulaInput> = z.lazy(() =>
  z.object({
    operator: z.string() as z.ZodType<FormulaOperator>,
    formulaValues: z.array(formulaValueInputSchema),
    columnOperationFilter: logicExpressionInputSchema.nullable().optional(),
    parentFormulaId: z.string().optional(),
    formulaString: z.string().optional(),
  }),
)

/**
 * Validates and converts Common types to GraphQL types with runtime safety
 */
export function toGraphQLParentFormulaInput(formula: CommonFormulaInput): ParentFormulaInput {
  const validated = formulaInputSchema.parse(formula)
  return validated as ParentFormulaInput
}

export function toGraphQLLogicExpressionInput(
  expr: CommonLogicExpressionInput,
): GqlLogicExpressionInput {
  const validated = logicExpressionInputSchema.parse(expr)
  return validated as GqlLogicExpressionInput
}

export function toGraphQLFormulaValueInput(value: CommonFormulaValueInput): GqlFormulaValueInput {
  const validated = formulaValueInputSchema.parse(value)
  return validated as GqlFormulaValueInput
}

export function toGraphQLLogicExpressionValueInput(
  value: CommonLogicExpressionValueInput,
): GqlLogicExpressionValueInput {
  const validated = logicExpressionValueInputSchema.parse(value)
  return validated as GqlLogicExpressionValueInput
}

export function toGraphQLFormulaConditionInput(
  condition: CommonFormulaConditionInput,
): GqlFormulaConditionInput {
  const validated = formulaConditionInputSchema.parse(condition)
  return validated as GqlFormulaConditionInput
}
